import { useRef, useEffect, useCallback, type MouseEventHandler } from 'react';
import { createSearchParams, useLocation, type Location } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';
import { setKeyword, clearKeyword } from '../ducks/index';
import { keywordSelector } from '../ducks/selectors';
import { selectIsDesktop, selectBlackFridayCampaignEnabled } from '~/components/brand/ducks/selectors';
import useCasinoRoutes from '~/components/router/use-casino-routes';
import SearchMobile from './search-mobile';
import SearchDesktop from './search-desktop';
import useUIContext from '~/ui/use-ui-context';
import { DesktopSearchWrapper } from '../search.styles';
import useTranslations from '~/components/translation/use-translations';
import { isSmallerSelector } from '~/components/layout/mediaquery';
import useSearchLocation from '~/hooks/use-search-location';
import { useCasinoSharedDispatch, useCasinoSharedSelector } from '~/components/casino-shared/ducks/types';

const textKeys = ['MOBILE_SEARCH_SEARCH_FOR_GAMES', 'MOBILE_CASINO_SEARCH_CANCEL'];

type Props = {
	onClear?: () => void;
	showClearButton: (keyword: string, isSearchLocation: boolean) => boolean;
};

const SearchContainer = ({ onClear, showClearButton }: Props) => {
	const dispatch = useCasinoSharedDispatch();
	const location = useLocation() as Location<{ internal: boolean; gameCode?: string }>;
	const router = useRouter();
	const uiContext = useUIContext();
	const keyword = useCasinoSharedSelector(keywordSelector);
	const isDesktop = useCasinoSharedSelector(selectIsDesktop);
	const isBlackFridayCampaignEnabled = useCasinoSharedSelector(selectBlackFridayCampaignEnabled);
	const inputEl = useRef<HTMLInputElement | null>(null);
	const { isSearchLocation } = useSearchLocation();
	const { translations } = useTranslations(textKeys);
	const smaller = useCasinoSharedSelector(isSmallerSelector);
	const { ROUTE_CASINO_EXPLORE, ROUTE_CASINO_SEARCH, ROUTE_LIVE_CASINO, ROUTE_LIVE_CASINO_SEARCH } =
		useCasinoRoutes();

	const getRedirectPath = useCallback(
		(pathname: string) => (pathname.includes(ROUTE_LIVE_CASINO_SEARCH) ? ROUTE_LIVE_CASINO : ROUTE_CASINO_EXPLORE),
		[ROUTE_CASINO_EXPLORE, ROUTE_LIVE_CASINO, ROUTE_LIVE_CASINO_SEARCH]
	);

	const getSearchPath = useCallback(
		(pathname: string) => (pathname.includes(ROUTE_LIVE_CASINO) ? ROUTE_LIVE_CASINO_SEARCH : ROUTE_CASINO_SEARCH),
		[ROUTE_CASINO_SEARCH, ROUTE_LIVE_CASINO, ROUTE_LIVE_CASINO_SEARCH]
	);

	const updateSearchKeyword = useCallback(
		(searchTerm: string) => {
			dispatch(setKeyword(searchTerm, isDesktop));
			const searchQuery = searchTerm ? `?${createSearchParams({ q: searchTerm }).toString()}` : '';
			// TODO (navigation) - use search params or redux for state
			router.replace(`${location.pathname}${searchQuery}`, {
				state: { ...location.state }
			});
		},
		[dispatch, isDesktop, location, router]
	);

	const cancel = useCallback(() => {
		if (location.state?.internal) {
			router.back();
		} else {
			router.replace(getRedirectPath(location.pathname));
			router.push(getSearchPath(location.pathname));
			router.back();
		}
		dispatch(clearKeyword(isDesktop));
		inputEl.current?.blur();
	}, [location.state?.internal, location.pathname, dispatch, isDesktop, router, getRedirectPath, getSearchPath]);

	const desktopCancelListener = useCallback(
		(event: MouseEvent) => {
			if (!isDesktop) return;
			if (!isSearchLocation) return;
			if (!(event.target instanceof HTMLElement)) return;
			const clickableTags = ['BUTTON', 'A', 'INPUT'];
			if (clickableTags.indexOf(event.target.tagName) > -1) return;
			if (event.target.matches('main *')) return;
			cancel();
		},
		[cancel, isDesktop, isSearchLocation]
	);

	const navigateToSearch: MouseEventHandler<HTMLInputElement | HTMLButtonElement> = event => {
		event.preventDefault();
		event.stopPropagation();
		event.nativeEvent.stopImmediatePropagation();
		window.scrollTo(0, 0);

		const { scrollbar } = uiContext;

		if (!isSearchLocation) {
			if (scrollbar?.current) {
				scrollbar.current.scrollToTop();
			}

			const searchQuery = keyword ? `?${createSearchParams({ q: keyword }).toString()}` : '';

			// TODO (navigation) - use search params or redux for state
			router.push(`${getSearchPath(location.pathname)}${searchQuery}`, {
				state: { internal: true }
			});
		}
	};

	const clear = () => {
		updateSearchKeyword('');
		if (onClear) {
			onClear();
		}
	};

	useEffect(() => {
		if (!isSearchLocation) {
			dispatch(clearKeyword(isDesktop));
		}
	}, [dispatch, isDesktop, isSearchLocation]);

	useEffect(() => {
		const isGameInfoModalOpen = location?.state?.gameCode;

		if (isSearchLocation && !isGameInfoModalOpen) {
			inputEl.current?.focus();
		} else {
			inputEl.current?.blur();
		}
	}, [isSearchLocation, location?.state?.gameCode]);

	useEffect(() => {
		window.addEventListener('click', desktopCancelListener, true);

		return () => {
			window.removeEventListener('click', desktopCancelListener, true);
		};
	}, [desktopCancelListener]);

	useEffect(() => {
		// it is possible to have a search keyword set up
		// but not have it visible in location.search
		// when navigating back from login sidebar in mobile
		if (isSearchLocation && !location.search && keyword) {
			updateSearchKeyword(keyword);
		}
	}, [keyword, location, updateSearchKeyword, isSearchLocation]);

	if (!smaller) {
		return (
			<DesktopSearchWrapper inSearchRoute={isSearchLocation}>
				<SearchDesktop
					ref={inputEl}
					updateSearchKeyword={updateSearchKeyword}
					navigateToSearch={navigateToSearch}
					clear={clear}
					placeholder={translations.MOBILE_SEARCH_SEARCH_FOR_GAMES}
					keyword={keyword}
					inSearchRoute={isSearchLocation}
					showClearButton={showClearButton(keyword, isSearchLocation)}
				/>
			</DesktopSearchWrapper>
		);
	}
	return (
		<SearchMobile
			ref={inputEl}
			updateSearchKeyword={updateSearchKeyword}
			navigateToSearch={navigateToSearch}
			clear={clear}
			cancel={cancel}
			keyword={keyword}
			inSearchRoute={isSearchLocation}
			cancelText={translations.MOBILE_CASINO_SEARCH_CANCEL}
			placeholder={translations.MOBILE_SEARCH_SEARCH_FOR_GAMES}
			blackFridayCampaignEnabled={isBlackFridayCampaignEnabled}
		/>
	);
};

export default SearchContainer;
